/**
 * Hook for loading and caching agent data
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { AgentConfig } from '../types/agent';

interface UseAgentsResult {
  agents: AgentConfig[];
  agentsById: Map<string, AgentConfig>;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

// refreshKey: bump this (e.g. a counter) to force a reload - lets a caller
// like PipelineCanvas pick up an agent edited elsewhere (the Agent Panel)
// without this hook needing to know anything about that panel.
export const useAgents = (refreshKey?: unknown): UseAgentsResult => {
  const [agents, setAgents] = useState<AgentConfig[]>([]);
  const [agentsById, setAgentsById] = useState<Map<string, AgentConfig>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedAgents = await apiClient.listAgents();
      setAgents(loadedAgents);

      // Create lookup map
      const map = new Map<string, AgentConfig>();
      loadedAgents.forEach(agent => {
        map.set(agent.id, agent);
      });
      setAgentsById(map);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return {
    agents,
    agentsById,
    loading,
    error,
    reload: loadAgents,
  };
};
